import React, { useState } from "react";
import styled from "styled-components";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { httpClient } from "settings/apolloClient";
import SERVER_REQUESTS_QUERY from "./graphql/serverRequestsQuery";
import RETRY_SERVER_REQUEST_MUTATION from "./graphql/retryServerRequestMutation";
import SEED_OPENING_INVENTORY_MUTATION from "./graphql/seedOpeningInventoryMutation";
import {
  PAGE_SIZE,
  SERVER_STATUS,
  Callout,
  Card,
  Scroll,
  Table,
  Th,
  Tr,
  Td,
  Mono,
  Method,
  Empty,
  C,
  StatusPill,
  Drawer,
  DrawerHead,
  DrawerBody,
  Eyebrow,
  DrawerTitle,
  HeadMeta,
  HeadGrow,
  CloseX,
  SectionLabel,
  KV,
  Pagination,
  PayloadSection,
  ResendButton,
  formatDateTime,
} from "./requestsShared";

const linkLabel = (r) => {
  if (r.concreteShipmentId) return `Carga #${r.concreteShipmentId}`;
  if (r.manualOperationId) return `Operação #${r.manualOperationId}`;
  if (r.inventoryEntryId) return `Estoque #${r.inventoryEntryId}`;
  return "—";
};

const SeedBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: 1px solid ${C.border};
`;

const SeedText = styled.div`
  flex: 1;
`;

const SeedTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${C.ink};
`;

const SeedHint = styled.div`
  font-size: 12px;
  color: ${C.muted};
  margin-top: 2px;
`;

const ServerRequestsPanel = ({ status }) => {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  const { data, loading, refetch } = useQuery(SERVER_REQUESTS_QUERY, {
    variables: {
      limit: PAGE_SIZE, offset: page * PAGE_SIZE, orderBy: "desc", status: status || null,
    },
    client: httpClient,
    fetchPolicy: "network-only",
  });

  const [retryServerRequest, { loading: retrying }] = useMutation(RETRY_SERVER_REQUEST_MUTATION, {
    client: httpClient,
  });

  const [seedOpening, { loading: seeding }] = useMutation(SEED_OPENING_INVENTORY_MUTATION, {
    client: httpClient,
  });
  const [seedResult, setSeedResult] = useState(null);

  const rows = (data && data.serverRequests) || [];
  const close = () => setSelected(null);

  const handleSeed = async () => {
    setSeedResult(null);
    try {
      const res = await seedOpening();
      const r = res.data && res.data.seedOpeningInventory;
      if (r) {
        setSeedResult({
          variant: r.errors > 0 ? "warn" : "ok",
          text: `Abertura semeada: ${r.ok}/${r.total} itens${r.errors ? `, ${r.errors} com erro` : ""}.`,
        });
      }
    } catch (err) {
      const gqlError = err && err.graphQLErrors && err.graphQLErrors[0];
      setSeedResult({
        variant: "danger",
        text: (gqlError && gqlError.message) || (err && err.message) || "Falha ao semear a abertura.",
      });
    } finally {
      refetch();
    }
  };

  const handleResend = async (r) => {
    try {
      const res = await retryServerRequest({ variables: { id: r.id } });
      const updated = res.data && res.data.retryServerRequest;
      if (updated) setSelected((prev) => (prev ? { ...prev, ...updated } : prev));
    } finally {
      refetch();
    }
  };

  return (
    <Card>
      <SeedBar>
        <SeedText>
          <SeedTitle>Abertura de estoque (Marco 0)</SeedTitle>
          <SeedHint>
            Espelha o saldo atual como saldo de abertura no relatório. Idempotente; bloqueado se houver carga em andamento.
          </SeedHint>
        </SeedText>
        <ResendButton disabled={seeding} onClick={handleSeed}>
          {seeding ? "Semeando…" : "Semear abertura"}
        </ResendButton>
      </SeedBar>
      {seedResult && (
        <Callout variant={seedResult.variant} style={{ margin: "12px 14px 0" }}>
          {seedResult.text}
        </Callout>
      )}
      <Scroll>
        <Table>
          <thead>
            <tr>
              <Th>Requisição</Th>
              <Th>Data/Hora</Th>
              <Th>ID</Th>
              <Th>Vínculo</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.id} selected={selected && selected.id === r.id} onClick={() => setSelected(r)}>
                <Td>
                  <Method method={r.method}>{r.method}</Method>
                  <Mono color={C.inkSoft}>{r.endpoint}</Mono>
                </Td>
                <Td><Mono>{formatDateTime(r.insertedAt)}</Mono></Td>
                <Td><Mono>{r.id}</Mono></Td>
                <Td>{linkLabel(r)}</Td>
                <Td><StatusPill config={SERVER_STATUS} value={r.status} /></Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Scroll>
      {!loading && rows.length === 0 && <Empty>Nenhuma requisição encontrada.</Empty>}
      <Pagination
        page={page}
        count={rows.length}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      {selected && (
        <Drawer onClose={close}>
          <DrawerHead>
            <HeadGrow>
              <Eyebrow>Envio ao servidor central</Eyebrow>
              <DrawerTitle>
                <Method method={selected.method}>{selected.method}</Method>
                {selected.endpoint}
              </DrawerTitle>
              <HeadMeta>
                <StatusPill config={SERVER_STATUS} value={selected.status} />
                <Mono>{`#${selected.id}`}</Mono>
                <ResendButton disabled={retrying} onClick={() => handleResend(selected)}>
                  {retrying ? "Reenviando…" : "Reenviar"}
                </ResendButton>
              </HeadMeta>
            </HeadGrow>
            <CloseX onClick={close}>✕</CloseX>
          </DrawerHead>
          <DrawerBody>
            <SectionLabel first>Resumo</SectionLabel>
            <KV>
              <dt>Método</dt>
              <dd>{selected.method}</dd>
              <dt>Endpoint</dt>
              <dd>{selected.endpoint}</dd>
              <dt>Vínculo</dt>
              <dd>{linkLabel(selected)}</dd>
              <dt>Criada em</dt>
              <dd>{formatDateTime(selected.insertedAt)}</dd>
              <dt>Tentativas</dt>
              <dd>{selected.attempts != null ? selected.attempts : "—"}</dd>
              <dt>Próx. tentativa</dt>
              <dd>{selected.nextRetryAt ? formatDateTime(selected.nextRetryAt) : "—"}</dd>
              {selected.lastError && [
                <dt key="last-error-label">Último erro</dt>,
                <dd key="last-error-value">{selected.lastError}</dd>,
              ]}
            </KV>
            <PayloadSection label="Payload enviado" note="coluna data" value={selected.data} />
          </DrawerBody>
        </Drawer>
      )}
    </Card>
  );
};

export default ServerRequestsPanel;
